/**
 * Number formatting utilities for financial inputs
 * Handles formatting with thousands separators while maintaining raw values
 */

/**
 * Format a number with thousands separators
 * @param {number|string} value - The number to format
 * @returns {string} Formatted number with commas (e.g., "1,234,567")
 */
export const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '';
  
  // Remove any existing formatting
  const cleanValue = String(value).replace(/[^0-9.-]/g, '');
  
  // Parse as number
  const num = parseFloat(cleanValue);
  
  if (isNaN(num)) return '';
  
  // Format with locale string for thousands separators
  return num.toLocaleString('en-US', {
    maximumFractionDigits: 0
  });
};

/**
 * Format a number as currency (with $ sign)
 * @param {number|string} value - The number to format
 * @returns {string} Formatted currency (e.g., "$1,234,567")
 */
export const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '';
  
  const formatted = formatNumber(value);
  return formatted ? `$${formatted}` : '';
};

/**
 * Parse a formatted string back to a raw number
 * @param {string} value - The formatted string (e.g., "1,234,567" or "$1,234,567")
 * @returns {number|null} The raw number value
 */
export const parseFormattedNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  
  // Remove currency symbols, commas, and whitespace
  const cleanValue = String(value).replace(/[$,\s]/g, '');
  
  const num = parseFloat(cleanValue);
  return isNaN(num) ? null : num;
};

/**
 * Format number for input display - handles user typing
 * Only formats complete numbers, allows partial input
 * @param {string} value - The input value
 * @returns {string} Formatted value
 */
export const formatInputValue = (value) => {
  if (value === null || value === undefined || value === '') return '';
  
  // Remove non-numeric characters except decimal point
  let cleanValue = String(value).replace(/[^0-9.]/g, '');
  
  // Handle multiple decimal points - keep only first
  const parts = cleanValue.split('.');
  if (parts.length > 2) {
    cleanValue = parts[0] + '.' + parts.slice(1).join('');
  }
  
  // Format the integer part with commas
  if (cleanValue.includes('.')) {
    const [intPart, decPart] = cleanValue.split('.');
    const formattedInt = intPart ? parseInt(intPart, 10).toLocaleString('en-US') : '';
    return `${formattedInt}.${decPart}`;
  }
  
  if (cleanValue === '') return '';
  
  return parseInt(cleanValue, 10).toLocaleString('en-US');
};

/**
 * Handle input change event - format value while preserving cursor position
 * @param {string} newValue - The new input value
 * @param {string} prevValue - The previous input value
 * @returns {object} { formatted: string, raw: number }
 */
export const handleNumberInput = (newValue) => {
  // Allow empty input
  if (newValue === '' || newValue === null || newValue === undefined) {
    return { formatted: '', raw: null };
  }
  
  // Get raw numeric value
  const raw = parseFormattedNumber(newValue);
  
  // Format for display
  const formatted = raw !== null ? formatNumber(raw) : '';
  
  return { formatted, raw };
};

export default {
  formatNumber,
  formatCurrency,
  parseFormattedNumber,
  formatInputValue,
  handleNumberInput
};
